import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  type PointerEvent as ReactPointerEvent,
} from 'react';

interface ZoomableImageProps {
  /** Snabb-laddad preview-URL (typ ~1600px). Visas omedelbart vid mount. */
  src: string;
  /** Full-res-URL (~5040px). Lazy-loadas FÖRST när användaren zoomar in.
   *  Om utelämnad används `src` även vid zoom (= lägre kvalitet vid 1:1). */
  fullSrc?: string;
  alt: string;
  // Naturlig pixel-storlek på bilden — används för 1:1-läget. Om okänt
  // försöker vi läsa naturalWidth/Height efter onLoad istället.
  naturalWidth?: number;
  naturalHeight?: number;
  // Anropas när zoom-läget toggleas (för t.ex. ändra cursor på parent eller
  // gömma navigation-pilar).
  onZoomChange?: (zoomed: boolean) => void;
  className?: string;
}

export interface ZoomableImageHandle {
  // Toggle zoom programmatically (för keyboard shortcuts från parent).
  toggle: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

// En zoomable image-container med click-to-toggle + pointer-pan.
//
// Logik:
//   • Default: bilden visas fit-to-screen (object-contain motsvarighet).
//   • Klick → "1:1"-läge: bilden renderas i naturlig pixelstorlek.
//   • När zoomad: pointer-position styr vilken del av bilden som visas
//     (mus rör sig vänster → vänsterkanten av bilden visas).
//   • Touch (iPad): tap → toggle, drag → pannar med fingret.
//   • Om bilden redan ryms i container i 1:1 → ingen zoom (det är ingen
//     mening att "zooma in" på en bild mindre än containern).
//
// Vi använder transform/translate för pan eftersom det är GPU-accelererat.
export const ZoomableImage = forwardRef<ZoomableImageHandle, ZoomableImageProps>(
  function ZoomableImage(
    { src, fullSrc, alt, naturalWidth, naturalHeight, onZoomChange, className },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const [isZoomed, setIsZoomed] = useState(false);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    // Mätt naturlig storlek (uppdateras från props OCH onLoad-fallback)
    const [natural, setNatural] = useState({ w: naturalWidth ?? 0, h: naturalHeight ?? 0 });
    // Pan-position (0..1 i bilden, 0.5 = mitten). Drag-läget skriver till denna.
    const [pan, setPan] = useState({ x: 0.5, y: 0.5 });

    // Lazy-load full-res när användaren zoomar in första gången.
    // När fullLoaded är true byter vi <img src> till fullSrc.
    const [fullLoaded, setFullLoaded] = useState(false);
    useEffect(() => {
      if (!fullSrc || fullLoaded || !isZoomed) return;
      // Pre-load full-res. När den är cachad i browsern är det säkert att
      // växla <img src> utan flicker.
      const img = new Image();
      img.decoding = 'async';
      img.src = fullSrc;
      img.onload = () => setFullLoaded(true);
    }, [fullSrc, fullLoaded, isZoomed]);

    // Reset full-loaded-state vid bildbyte
    useEffect(() => {
      setFullLoaded(false);
    }, [src, fullSrc]);

    // Effektiv src — full om laddad, annars preview
    const effectiveSrc = fullLoaded && fullSrc ? fullSrc : src;

    // Drag-state för touch-pan
    const dragRef = useRef<{
      startX: number;
      startY: number;
      startPanX: number;
      startPanY: number;
      pointerId: number;
      moved: boolean;
    } | null>(null);

    // Sync från props (när bilden byts ut → reset zoom)
    useEffect(() => {
      setNatural({ w: naturalWidth ?? 0, h: naturalHeight ?? 0 });
      setIsZoomed(false);
      setPan({ x: 0.5, y: 0.5 });
    }, [src, naturalWidth, naturalHeight]);

    // Notifiera parent vid zoom-toggle
    useEffect(() => {
      onZoomChange?.(isZoomed);
    }, [isZoomed, onZoomChange]);

    // Mät container-storlek (för att räkna ut om zoom är meningsfull)
    useLayoutEffect(() => {
      if (!containerRef.current) return;
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
        }
      });
      ro.observe(containerRef.current);
      // Initial measurement (ResizeObserver fires async)
      const r = containerRef.current.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
      return () => ro.disconnect();
    }, []);

    // Är bilden större än containern (i någon dimension)? Annars är zoom no-op.
    const canZoom = useMemo(() => {
      if (!natural.w || !natural.h || !containerSize.w || !containerSize.h) return false;
      return natural.w > containerSize.w || natural.h > containerSize.h;
    }, [natural, containerSize]);

    // Beräkna translate för panorering. När bilden är 1:1 och bredare/högre än
    // containern: vi vill att pan.x = 0 → vänsterkant syns, pan.x = 1 → höger.
    //
    // Bilden positioneras absolut med top/left = 0 default. Translate = pan
    // gånger overflow-distansen åt motsatt håll.
    const transform = useMemo(() => {
      if (!isZoomed || !canZoom) return undefined;
      const overflowX = Math.max(0, natural.w - containerSize.w);
      const overflowY = Math.max(0, natural.h - containerSize.h);
      const tx = -pan.x * overflowX;
      const ty = -pan.y * overflowY;
      return `translate3d(${tx}px, ${ty}px, 0)`;
    }, [isZoomed, canZoom, natural, containerSize, pan]);

    // ── Pointer handlers ────────────────────────────────────
    // Vi använder Pointer Events som täcker mus + touch + pen. För touch
    // implementerar vi drag-pan (fingret = pan-position). För mus använder vi
    // mouseMove för "live" pan följande markörens position.

    const handlePointerDown = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        if (!isZoomed || !canZoom) return;
        // Bara starta drag för touch/pen — för mus använder vi ren hover.
        if (e.pointerType === 'mouse') return;
        dragRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          startPanX: pan.x,
          startPanY: pan.y,
          pointerId: e.pointerId,
          moved: false,
        };
        try {
          (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      },
      [isZoomed, canZoom, pan],
    );

    const handlePointerMove = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        if (!isZoomed || !canZoom || !containerRef.current) return;
        if (e.pointerType === 'mouse') {
          // Mus: pan följer cursor-position direkt (0..1 över containern).
          const rect = containerRef.current.getBoundingClientRect();
          const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
          const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
          setPan({ x, y });
          return;
        }
        // Touch/pen: drag-pan baserat på rörelse i pixel × overflow.
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
        const overflowX = Math.max(1, natural.w - containerSize.w);
        const overflowY = Math.max(1, natural.h - containerSize.h);
        // Drag åt höger → vi vill se vänster delen av bilden → pan.x ska minska.
        const nextX = clamp(drag.startPanX - dx / overflowX, 0, 1);
        const nextY = clamp(drag.startPanY - dy / overflowY, 0, 1);
        setPan({ x: nextX, y: nextY });
      },
      [isZoomed, canZoom, natural, containerSize],
    );

    const handlePointerUp = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        try {
          (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        dragRef.current = null;
        // Om touch-pekaren rörde sig hint > 4px räknar vi det som drag (inte
        // tap) — tap används för toggle och hanteras av onClick. Inget mer att
        // göra här; onClick får sista ordet.
        void drag;
      },
      [],
    );

    // ── Click → toggle zoom ────────────────────────────────
    const handleClick = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        // Om vi precis avslutade en touch-drag (rörde > 4px) ska klick INTE
        // toggla. Browsern firar både pointerup och click för touch, så vi
        // använder en ref-flag.
        if (dragRef.current?.moved) return;
        e.preventDefault();
        if (!canZoom) return;
        setIsZoomed((z) => {
          const next = !z;
          if (next && containerRef.current) {
            // Initial pan utgår från där användaren klickade — så känns det
            // som att man "zoomar in på den punkten" istället för mitten.
            const rect = containerRef.current.getBoundingClientRect();
            const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
            setPan({ x, y });
          } else {
            setPan({ x: 0.5, y: 0.5 });
          }
          return next;
        });
      },
      [canZoom],
    );

    // ── Imperative API för parent (keyboard shortcuts) ────
    useImperativeHandle(
      ref,
      () => ({
        toggle: () => {
          if (canZoom) setIsZoomed((z) => !z);
        },
        zoomIn: () => {
          if (canZoom) setIsZoomed(true);
        },
        zoomOut: () => {
          setIsZoomed(false);
          setPan({ x: 0.5, y: 0.5 });
        },
      }),
      [canZoom],
    );

    // ── onLoad fallback för natural-storlek ───────────────
    const handleImgLoad = useCallback(() => {
      const el = imgRef.current;
      if (!el) return;
      if (!natural.w || !natural.h) {
        setNatural({ w: el.naturalWidth, h: el.naturalHeight });
      }
    }, [natural]);

    const cursor = !canZoom
      ? 'default'
      : isZoomed
        ? 'zoom-out'
        : 'zoom-in';

    return (
      <div
        ref={containerRef}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={[
          'relative w-full h-full overflow-hidden flex items-center justify-center select-none',
          className ?? '',
        ].join(' ')}
        style={{
          cursor,
          // Förhindra default touch-pan så vi kan implementera vår egen
          touchAction: isZoomed ? 'none' : 'manipulation',
        }}
      >
        {isZoomed && canZoom ? (
          <img
            ref={imgRef}
            key={effectiveSrc}
            src={effectiveSrc}
            alt={alt}
            onLoad={handleImgLoad}
            draggable={false}
            className="absolute top-0 left-0 max-w-none max-h-none pointer-events-none rounded shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)]"
            style={{
              width: natural.w,
              height: natural.h,
              transform,
              transition: 'transform 60ms linear',
              willChange: 'transform',
            }}
          />
        ) : (
          <img
            ref={imgRef}
            key={effectiveSrc}
            src={effectiveSrc}
            alt={alt}
            onLoad={handleImgLoad}
            draggable={false}
            className="max-w-full max-h-full object-contain pointer-events-none rounded shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)]"
            style={{
              aspectRatio:
                natural.w && natural.h ? `${natural.w} / ${natural.h}` : undefined,
            }}
          />
        )}
      </div>
    );
  },
);
